import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { Users, UserCheck, TrendingUp, ShoppingBag } from 'lucide-react';
import { toast } from 'react-toastify';
import { customerAnalyticsAPI } from '../../services';

const fmt = (n) =>
  n >= 1_000_000
    ? (n / 1_000_000).toFixed(1) + 'M'
    : n >= 1_000
    ? (n / 1_000).toFixed(0) + 'k'
    : String(Math.round(n));

const fmtCurrency = (n) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(n);

const pct = (n, total) => (total > 0 ? ((n / total) * 100).toFixed(1) + '%' : '0%');

function SegmentCard({ title, icon: Icon, color, data, total, badgeVariant }) {
  const customerPct = pct(data?.customer_count ?? 0, total);
  return (
    <Card className="h-100 shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-2"
            style={{ width: 36, height: 36, background: color + '20' }}
          >
            <Icon size={18} color={color} />
          </div>
          <div>
            <div className="fw-semibold">{title}</div>
            <Badge bg={badgeVariant} className="mt-0" style={{ fontSize: '0.7rem' }}>
              {customerPct} tổng khách
            </Badge>
          </div>
        </div>
        <Row className="g-2 text-center">
          <Col xs={4}>
            <div className="fw-bold fs-5" style={{ color }}>
              {fmt(data?.customer_count ?? 0)}
            </div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}>Khách</div>
          </Col>
          <Col xs={4}>
            <div className="fw-bold fs-5">{fmt(data?.order_count ?? 0)}</div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}>Đơn</div>
          </Col>
          <Col xs={4}>
            <div className="fw-bold fs-5">{fmt(data?.revenue ?? 0)}</div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}>Doanh thu</div>
          </Col>
        </Row>
        <hr className="my-2" />
        <div className="text-center text-muted small">
          AOV: <strong>{fmtCurrency(data?.avg_order_value ?? 0)}</strong>
        </div>
      </Card.Body>
    </Card>
  );
}

function ProductTable({ title, products, loading, color }) {
  return (
    <Card className="shadow-sm">
      <Card.Header className="py-2 px-3 d-flex align-items-center gap-2" style={{ background: color + '15' }}>
        <ShoppingBag size={15} color={color} />
        <strong style={{ fontSize: '0.88rem', color }}>{title}</strong>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="text-center py-4">
            <Spinner size="sm" />
          </div>
        ) : !products?.length ? (
          <div className="text-center text-muted py-4 small">Không có dữ liệu</div>
        ) : (
          <Table size="sm" className="mb-0" hover>
            <thead>
              <tr className="table-light">
                <th style={{ width: 30 }}>#</th>
                <th>Món</th>
                <th className="text-end">Khách</th>
                <th className="text-end">Doanh thu</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={p.menu_item_id}>
                  <td className="text-muted">{i + 1}</td>
                  <td>
                    <div style={{ fontSize: '0.85rem' }}>{p.menu_item_name}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {p.total_qty} lượt
                    </div>
                  </td>
                  <td className="text-end">{p.customer_count}</td>
                  <td className="text-end">
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{fmt(p.revenue)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default function CampaignEvaluationTab({ queryParams }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerAnalyticsAPI.getCampaignEvaluation(queryParams);
      setData(res);
    } catch (err) {
      console.error(err);
      toast.error('Không tải được đánh giá chiến dịch');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="text-center py-5">
        <Spinner />
        <div className="mt-2 text-muted small">Đang phân tích...</div>
      </div>
    );
  }

  const totalCustomers = data?.total_customers ?? 0;
  const newRatio = data ? Math.round((data.new_customer_ratio ?? 0) * 100) : 0;
  const period = data?.period;

  return (
    <div>
      {/* Period info */}
      {period && (
        <Alert variant="light" className="py-2 px-3 mb-3 border small text-muted">
          Phân tích đơn COMPLETED từ <strong>{period.from_date}</strong> đến{' '}
          <strong>{period.to_date}</strong>. Khách &quot;mới&quot; = SĐT chưa mua bất kỳ chi nhánh
          nào trước ngày bắt đầu kỳ.
        </Alert>
      )}

      {/* Summary cards */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <SegmentCard
            title="Khách mới"
            icon={Users}
            color="#0d6efd"
            data={data?.new_customers}
            total={totalCustomers}
            badgeVariant="primary"
          />
        </Col>
        <Col md={4}>
          <SegmentCard
            title="Khách quay lại"
            icon={UserCheck}
            color="#198754"
            data={data?.returning_customers}
            total={totalCustomers}
            badgeVariant="success"
          />
        </Col>
        <Col md={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex flex-column justify-content-center">
              <div className="d-flex align-items-center mb-2">
                <TrendingUp size={18} className="me-2 text-warning" />
                <span className="fw-semibold">Tổng kết kỳ</span>
              </div>
              <Row className="g-2 text-center">
                <Col xs={6}>
                  <div className="fw-bold fs-5">{fmt(totalCustomers)}</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>Tổng khách</div>
                </Col>
                <Col xs={6}>
                  <div className="fw-bold fs-5">{fmt(data?.total_orders ?? 0)}</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>Tổng đơn</div>
                </Col>
              </Row>
              <hr className="my-2" />
              <div className="text-center small">
                Doanh thu: <strong>{fmtCurrency(data?.total_revenue ?? 0)}</strong>
              </div>
              <div className="text-center mt-1">
                <Badge bg={newRatio > 50 ? 'primary' : 'success'} style={{ fontSize: '0.8rem' }}>
                  {newRatio}% khách mới
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top products per segment */}
      <Row className="g-3">
        <Col md={6}>
          <ProductTable
            title="Khách mới thường mua"
            products={data?.top_products_new}
            loading={loading}
            color="#0d6efd"
          />
        </Col>
        <Col md={6}>
          <ProductTable
            title="Khách quay lại thường mua"
            products={data?.top_products_returning}
            loading={loading}
            color="#198754"
          />
        </Col>
      </Row>
    </div>
  );
}
